// @flow

import { getJSON } from 'redux-api-middleware';
import { I18n } from '@shopify/react-i18n';
import { processRes } from 'src/helpers/actions-helper';
import { DASHBOARD_BLOCKS_DEFAULT } from 'src/constants/dashboard-layout.js';
import { RECOMMENDED_APPS } from 'src/constants/recommended-apps';

import apiService from 'src/services/api-service';
import {
  GET_DASHBOARD_BLOCKS_REQUEST,
  GET_DASHBOARD_BLOCKS_SUCCESS,
  GET_DASHBOARD_BLOCKS_FAILURE,
  GET_CAMPAIGN_BANNERS_REQUEST,
  GET_CAMPAIGN_BANNERS_SUCCESS,
  GET_CAMPAIGN_BANNERS_FAILURE,
  GET_IN_APP_SPOTLIGHT_REQUEST,
  GET_IN_APP_SPOTLIGHT_SUCCESS,
  GET_IN_APP_SPOTLIGHT_FAILURE,
  GET_DASHBOARD_COMPONENTS_REQUEST,
  GET_DASHBOARD_COMPONENTS_SUCCESS,
  GET_DASHBOARD_COMPONENTS_FAILURE,
  GET_CHANGE_LOGS_REQUEST,
  GET_CHANGE_LOGS_SUCCESS,
  GET_CHANGE_LOGS_FAILURE,
  GET_CROSS_SELL_UP_SELL_REQUEST,
  GET_CROSS_SELL_UP_SELL_SUCCESS,
  GET_CROSS_SELL_UP_SELL_FAILURE,
  FEEDBACK_REQUEST,
  FEEDBACK_SUCCESS,
  FEEDBACK_FAILURE,
  PROCESS_DISMISS_BLOCK_REQUEST,
  PROCESS_DISMISS_BLOCK_SUCCESS,
  PROCESS_DISMISS_BLOCK_FAILURE,
  CONFIRM_DISMISS_BLOCK,
  UPDATE_DASHBOARD_BLOCK,
  SET_SHOW_INSTALLATION_STATUS_BANNER_REQUEST,
  SET_SHOW_INSTALLATION_STATUS_BANNER_SUCCESS,
  SET_SHOW_INSTALLATION_STATUS_BANNER_FAILURE,
  IM_IN_FEEDBACK_5TARS,
  DISABLE_SEARCH_ONBOARDING_REQUEST,
  DISABLE_SEARCH_ONBOARDING_SUCCESS,
  DISABLE_SEARCH_ONBOARDING_FAILURE
} from 'stores/flow-type/dashboard-v3.type';

/**
 * call to admin dashboard block api
 * @return {object} The RSAA object for API middleware
 */
export const getDashboardBlocks = () => {
  return apiService.sendRequest('admin-v3.dashboard-blocks.get-dashboard-blocks', {}, [
    GET_DASHBOARD_BLOCKS_REQUEST,
    {
      type: GET_DASHBOARD_BLOCKS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);
        return { data };
      }
    },
    {
      type: GET_DASHBOARD_BLOCKS_FAILURE,
      payload: async () => {
        return { data: DASHBOARD_BLOCKS_DEFAULT };
      }
    }
  ]);
};

/**
 * call to admin get campaign banners api
 * @return {object} The RSAA object for API middleware
 */
export const getCampaignBanners = () => {
  return apiService.sendRequest('admin-v3.dashboard-blocks.get-campaign-banners', {}, [
    GET_CAMPAIGN_BANNERS_REQUEST,
    {
      type: GET_CAMPAIGN_BANNERS_SUCCESS,
      payload: async (action, state, res) => {
        const { data } = await getJSON(res);
        return { data };
      }
    },
    {
      type: GET_CAMPAIGN_BANNERS_FAILURE,
      payload: async () => {
        return { data: [] };
      }
    }
  ]);
};

/**
 * call to admin get-in-app-spotlight api
 * @return {object} The RSAA object for API middleware
 */
export const getInAppSpotlight = () => {
  return apiService.sendRequest('admin-v3.dashboard-blocks.get-in-app-spotlight', {}, [
    GET_IN_APP_SPOTLIGHT_REQUEST,
    {
      type: GET_IN_APP_SPOTLIGHT_SUCCESS,
      payload: async (action, state, res) => {
        const { data } = await getJSON(res);
        return { data };
      }
    },
    {
      type: GET_IN_APP_SPOTLIGHT_FAILURE,
      payload: async () => {
        return { data: RECOMMENDED_APPS };
      }
    }
  ]);
};

/**
 * call to admin dashboard component api
 * @return {object} The RSAA object for API middleware
 */
export const getDashboardComponents = (language: string, i18n: I18n) => {
  const queries = { language };
  return apiService.sendRequest('admin-v3.dashboard-blocks.get-dashboard-components', { queries }, [
    GET_DASHBOARD_COMPONENTS_REQUEST,
    {
      type: GET_DASHBOARD_COMPONENTS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_DASHBOARD_COMPONENTS_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.DashboardV3.Actions.getDashboardComponents'),
          i18n
        )
    }
  ]);
};

/**
 * call to get change logs
 * @return {object} The RSAA object for API middleware
 */
export const getChangeLogs = (language: string, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.dashboard-blocks.get-change-logs', {}, [
    GET_CHANGE_LOGS_REQUEST,
    {
      type: GET_CHANGE_LOGS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_CHANGE_LOGS_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.DashboardV3.Actions.getChangeLogs'),
          i18n
        )
    }
  ]);
};

/**
 * call to get essential features for every winning store
 * @return {object} The RSAA object for API middleware
 */
export const getCrossSellUpSellList = (i18n: I18n) => {
  return apiService.sendRequest('admin-v3.dashboard-blocks.get-cross-sell-up-sell', {}, [
    GET_CROSS_SELL_UP_SELL_REQUEST,
    {
      type: GET_CROSS_SELL_UP_SELL_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_CROSS_SELL_UP_SELL_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.DashboardV3.Actions.getCrossSellUpSell'),
          i18n
        )
    }
  ]);
};
/**
 * send feedback from dashboad
 * @return {object} The RSAA object for API middleware
 */
export const sendFeedback = (body: Object, blockName: string, i18n: I18n) => {
  return apiService.sendRequest(
    'admin-v3.dashboard-blocks.feedback-dashboard-block',
    { params: { blockId: blockName }, body },
    [
      { type: FEEDBACK_REQUEST, payload: { blockName } },
      {
        type: FEEDBACK_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { ...data, blockName };
        }
      },
      {
        type: FEEDBACK_FAILURE,
        payload: async (action, state, res) => {
          const data = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.DashboardV3.Actions.sendFeedback'),
            i18n
          );
          return { ...data, blockName };
        }
      }
    ]
  );
};

/**
 * update dashboard blocks list after dismiss
 */
export const confirmDismissBlock = (blockName: string) => {
  return { type: CONFIRM_DISMISS_BLOCK, payload: { blockName } };
};

/**
 * call to dismiss a block in dashboard blocks list
 * @return {object} The RSAA object for API middleware
 */

export const processDismissBlock = (blockName: string, i18n: I18n) => {
  return apiService.sendRequest(
    'admin-v3.dashboard-blocks.change-dashboard-block',
    { body: { [blockName]: false } },
    [
      { type: PROCESS_DISMISS_BLOCK_REQUEST, payload: { blockName } },
      {
        type: PROCESS_DISMISS_BLOCK_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data, blockName };
        }
      },
      {
        type: PROCESS_DISMISS_BLOCK_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.DashboardV3.Actions.processDismissBlock'),
            i18n
          )
      }
    ]
  );
};

export const disableSearchOnboarding = (i18n: I18n) => {
  return apiService.sendRequest('admin-v3.dashboard-blocks.disable-search-onboarding', {}, [
    DISABLE_SEARCH_ONBOARDING_REQUEST,
    {
      type: DISABLE_SEARCH_ONBOARDING_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: DISABLE_SEARCH_ONBOARDING_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.DashboardV3.Actions.setShowInstallationStatusBanner'),
          i18n
        )
    }
  ]);
};

export const setShowInstallationStatusBanner = (
  data: { enable: boolean, type: string },
  i18n: I18n
) => {
  return apiService.sendRequest(
    'admin-v3.dashboard-blocks.set-show-installation-status-banner',
    { body: data },
    [
      SET_SHOW_INSTALLATION_STATUS_BANNER_REQUEST,
      {
        type: SET_SHOW_INSTALLATION_STATUS_BANNER_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: SET_SHOW_INSTALLATION_STATUS_BANNER_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.DashboardV3.Actions.setShowInstallationStatusBanner'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to update dashboard blocks list
 * @return {object} The RSAA object for API middleware
 */

export const updateDashboardBlock = (blocks: Object) => {
  return { type: UPDATE_DASHBOARD_BLOCK, payload: { blocks } };
};

/**
 * Open popup i'm in when after customer rating 5 stars
 * @returns
 */
export const processImInFeedback5Stars = () => {
  return { type: IM_IN_FEEDBACK_5TARS, payload: {} };
};
